/**
 * @file color-field-model.ts
 * @description Модель поля цвета `ColorField` (phase19.16, партия SB120 —
 * свобода выбора; uiux-sb120-fields §3): из пропсов двух режимов — одни и те
 * же ячейки, контраст текущего выбора и «Взять читаемый». Разметка в
 * компоненте одна, разница режимов — здесь:
 *  - text — «Авто» → роли темы → «Свой цвет»; контраст с фоном под
 *    элементом (`elementBg`), фото — «проверьте на канве»;
 *  - fill — («Без фона») → роли-заливки → «Свой цвет»; контраст текста,
 *    который подберёт платформа, на заливке; вместе с заливкой — тон.
 */

import {
  resolveSurfaceFill,
  type SiteAutoTextColor,
  type SiteColorRole,
  type SiteColors,
  type SiteFillRole,
  type SiteSurfaceFill,
  type SiteSurfaceTone,
} from "@chaiberi/shared";

import { autoTextColor, fillContrast, opaqueBg, READABLE_MIN, textContrast } from "./color-contrast";

/**
 * Цвет текста элемента: авто (по фону), роль темы или свой цвет — тот же
 * тип, что `SiteAutoTextColor` shared (SB120 этап 4, db-design-sb120-stage4
 * §1.1), с сужением ролей `R`: `ColorTextValue<SiteTextColorRole>` ≡
 * `SiteAutoTextColor`.
 */
export type ColorTextValue<R extends SiteColorRole = SiteColorRole> = Exclude<SiteAutoTextColor, { kind: "role" }> | { kind: "role"; role: R };

interface BaseProps<R extends SiteColorRole> {
  label: string;
  /** Подпись уже есть у группы — над полем не повторять (aria-label остаётся). */
  hideLabel?: boolean;
  /** Роли темы в порядке ячеек и их подписи. */
  roles: readonly R[];
  labels: Readonly<Record<R, string>>;
  colors: SiteColors;
  /** «↺» справа от подписи — вернуть наследуемое значение. */
  onReset?: () => void;
  /** Подпись «↺»: «авто», «как у сайта». */
  inheritLabel?: string;
  /** Ячейка «Свой цвет» (по умолчанию есть). */
  custom?: boolean;
  /** Строка контраста под полем (по умолчанию есть; у подложки корзины текста нет — без неё). */
  contrast?: boolean;
}

export interface ColorFieldTextProps<R extends SiteColorRole> extends BaseProps<R> {
  mode: "text";
  value: ColorTextValue<R>;
  onChange: (value: ColorTextValue<R>) => void;
  /** Фон под элементом: `#hex`, `"photo"` (контраст зависит от снимка) или неизвестен. */
  elementBg?: string;
  /** Подпись ячейки «Авто». */
  autoLabel?: string;
  /**
   * Цвет, который платформа ставит при «Авто», если он не «Текст / На
   * тёмном по тону фона» (SB120 этап 3а: текст кнопки с заливкой —
   * `pickButtonText`, контурной и ссылки — цвет ссылки секции).
   */
  autoColor?: string;
  /**
   * Ячейки-наборы вместо «Авто» (SB120 этап 1: «Тёмный набор» / «Светлый
   * набор» текста на фото фона — uiux-sb120-stage1 §2.3). Выбранность и
   * запись — у вызывающего; значение поля при наборе — `{ kind: "auto" }`.
   */
  presets?: ReadonlyArray<{ id: string; label: string; color: string; active: boolean; onSelect: () => void }>;
}

export interface ColorFieldFillProps extends BaseProps<SiteFillRole> {
  mode: "fill";
  /** `null` — без фона (или ничего не выбрано, если ячейки «Без фона» нет). */
  value: SiteSurfaceFill | null;
  /** Тон поверхности — вместе с заливкой (`header.tone`); у «Без фона» — `null`. */
  onChange: (fill: SiteSurfaceFill | null, tone: SiteSurfaceTone | null) => void;
  /**
   * Первая ячейка «Без фона» или ссылка на чужую заливку («Как шапка»):
   * `swatch` — цвет кружка (заливка, которую возьмёт поле), без него —
   * пустой пунктирный кружок «без фона».
   */
  none?: { label: string; swatch?: string };
}

export type ColorFieldProps<R extends SiteColorRole> = ColorFieldTextProps<R> | ColorFieldFillProps;

/** Ячейка поля: вид кружка, подпись, выбрана ли. */
export interface SwatchCell {
  id: string;
  kind: "auto" | "none" | "color";
  label: string;
  color?: string;
  active: boolean;
  onSelect: () => void;
}

export interface ColorFieldModel {
  cells: SwatchCell[];
  /** Выбранный свой цвет; `null` — выбран не он. */
  customColor: string | null;
  setCustom: (hex: string) => void;
  /** С чего открыть окно «Свой цвет». */
  seed: string;
  /** Контраст текущего выбора; `null` — не с чем сравнить. */
  ratio: number | null;
  /** Под элементом фото — число не считаем. */
  photo: boolean;
  /** Первая читаемая роль (≥ 4,5), кроме выбранной. */
  readable: { label: string; select: () => void } | null;
  /** Фон образца окна (режим text). */
  sampleBg?: string;
}

/** Первая роль с контрастом не ниже нормы; выбранную не предлагаем. */
function firstReadable<R extends string>(
  roles: readonly R[],
  labels: Readonly<Record<R, string>>,
  current: R | null,
  measure: (role: R) => number | null,
  select: (role: R) => void,
): ColorFieldModel["readable"] {
  const role = roles.find((r) => r !== current && (measure(r) ?? 0) >= READABLE_MIN);
  return role ? { label: labels[role], select: () => select(role) } : null;
}

export function textFieldModel<R extends SiteColorRole>(p: ColorFieldTextProps<R>): ColorFieldModel {
  const { value, colors, roles, labels, onChange } = p;
  const photo = p.elementBg === "photo";
  const bg = p.elementBg && !photo ? opaqueBg(p.elementBg, colors) : null;
  const colorOf = (v: ColorTextValue<R>): string | null =>
    v.kind === "role" ? colors[v.role] : v.kind === "custom" ? v.color : bg ? (p.autoColor ?? autoTextColor(bg, colors)) : null;
  const measure = (v: ColorTextValue<R>): number | null => {
    const fg = bg ? colorOf(v) : null;
    return fg && bg ? textContrast(fg, bg, colors) : null;
  };
  const selectRole = (role: R) => onChange({ kind: "role", role });
  const current = value.kind === "role" ? value.role : null;
  const head: SwatchCell[] = p.presets
    ? p.presets.map((c) => ({ id: c.id, kind: "color" as const, label: c.label, color: c.color, active: value.kind === "auto" && c.active, onSelect: c.onSelect }))
    : [{ id: "auto", kind: "auto", label: p.autoLabel ?? "Авто", active: value.kind === "auto", onSelect: () => onChange({ kind: "auto" }) }];
  const cells: SwatchCell[] = [
    ...head,
    ...roles.map((role) => ({ id: role, kind: "color" as const, label: labels[role], color: colors[role], active: current === role, onSelect: () => selectRole(role) })),
  ];
  const ratio = measure(value);
  return {
    cells,
    customColor: value.kind === "custom" ? value.color : null,
    setCustom: (color) => onChange({ kind: "custom", color }),
    seed: colorOf(value) ?? colors.text,
    ratio,
    photo,
    readable: ratio !== null && ratio < READABLE_MIN ? firstReadable(roles, labels, current, (role) => measure({ kind: "role", role }), selectRole) : null,
    sampleBg: bg ?? undefined,
  };
}

export function fillFieldModel(p: ColorFieldFillProps): ColorFieldModel {
  const { value, colors, roles, labels, onChange } = p;
  const emit = (fill: SiteSurfaceFill) => onChange(fill, resolveSurfaceFill(fill, { colors }).tone);
  const selectRole = (role: SiteFillRole) => emit({ kind: "role", role });
  const current = value?.kind === "role" ? value.role : null;
  const cells: SwatchCell[] = [
    ...(p.none ? [{ id: "none", kind: "none" as const, label: p.none.label, color: p.none.swatch, active: value === null, onSelect: () => onChange(null, null) }] : []),
    ...roles.map((role) => ({ id: role, kind: "color" as const, label: labels[role], color: colors[role], active: current === role, onSelect: () => selectRole(role) })),
  ];
  const ratio = value ? fillContrast(value, colors) : null;
  return {
    cells,
    customColor: value?.kind === "custom" ? value.color : null,
    setCustom: (color) => emit({ kind: "custom", color }),
    seed: value?.kind === "custom" ? value.color : value?.kind === "role" ? colors[value.role] : colors.panelBg,
    ratio,
    photo: false,
    readable:
      ratio !== null && ratio < READABLE_MIN ? firstReadable(roles, labels, current, (role) => fillContrast({ kind: "role", role }, colors), selectRole) : null,
  };
}
